import type {ReactElement, RefObject} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import type {AiVoicePanelProps} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import type {VoiceLanguage} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {AiVoicePanelDataKey, AiVoicePanelErrorState, AiVoicePanelGenerationState, DEFAULT_VOICE_LANGUAGE} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import type {PanelContentProps, PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import {Panel, PanelContent, PanelFooter, PanelHeader} from '@Components/panel';
import {PanelFooterPrimaryDefaultAction} from '@Components/panel/components/panel-footer-primary-default-action/panel-footer-primary-default-action';
import {Size} from '@Components/button';
import {isMobile} from 'react-device-detect';
import {IconSize} from '@Components/icon-v2/icon.types';
import {AiButton} from '@Components/ai-button';
import {AIButtonShape, AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {getCreditCostForVoiceId, getDefaultVoiceIdForLanguage, getLanguageForVoiceId, getLoadingKeyForAiVoice} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {
  GENERATE_SPEECH_DAILY_LIMIT_REACHED,
  GENERATE_SPEECH_INSUFFICIENT_CREDITS,
  generateSpeech,
  getUrlForGeneratedAiSpeech,
  showTextToSpeechUpsellDialog,
} from '@Libraries/text-to-speech.library';
import {getRemainingAiUsages} from '@Libraries/pmw-stock-media-library';
import {triggerBuyMoreCredits} from '@Components/poster-editor/library/poster-editor-open-modals';
import {addItemsToGrid, deleteGridItems, getSelectedGridItems} from '@Components/base-grid';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {GENERATED_VOICE_GRID_ID} from '@Panels/ai-voice-panel/components/generated-voice-list/generated-voice-list.types';
import styles from './ai-voice-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {noop} from '@Utils/general.util';
import {getErrorMessage} from '@Utils/error.util';
import {isUserPremium} from '@Libraries/user.library';
import {isPremiumPlusUser} from '@Utils/user.util';
import {updatePanelData, updatePanelSizeData} from '@Components/panel/panel-reducer';
import {openErrorModal} from '@Modals/error-modal';
import type {PanelMessageProp} from '@Components/panel/panel.types';
import {PANEL_MESSAGE_TYPE} from '@Components/panel/panel.types';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {AiVoiceState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {updateAiVoiceState} from '@Components/poster-editor/poster-editor-reducer';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {updateSelectedAiVoiceProfileId} from '@/reducers/user-preferences.reducer';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {AiVoiceView} from '@Panels/ai-voice-panel/components/ai-voice-view';
import {AiWriterPopoverView} from '@Panels/ai-voice-panel/components/ai-writer-popover-view';
import {AiWriterPanelView} from '@Panels/ai-voice-panel/components/ai-writer-panel-view';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AI_VOICE_HEIGHT, AI_VOICE_WRITER_HEIGHT} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.types';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {Text, TextSize} from '@Components/text';
import {GeneratedVoiceItem} from '@Panels/ai-voice-panel/components/generated-voice-item';
import {getGeneratedVoiceItemProps} from '@Panels/ai-voice-panel/components/generated-voice-item/generated-voice-item.library';
import {v4 as uuidv4} from 'uuid';
import {getAiVoicePanelId} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.library';

export default function AiVoicePanel({isOpenedInPopOver = false, showBackButton = true, ...props}: AiVoicePanelProps): ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const aiWriterButtonRef = useRef<HTMLButtonElement>(null);
  const [errorState, setErrorState] = useState<AiVoicePanelErrorState | undefined>(undefined);
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? getAiVoicePanelId('AI_VOICE_PANEL'));
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(gridRef, !!gridRef.current);
  const {actions, state} = useAiPromptBox(panelId.current);
  const onClose = useCustomPanelClose(getAiVoicePanelId(panelId.current), props.onClose?.bind(null) ?? noop, props.hideOnClose);
  const getPanelMessage = useMemo((): PanelMessageProp | undefined => {
    if (errorState === AiVoicePanelErrorState.DAILY_LIMIT_REACHED && props.isFullScreen) {
      return {
        message: window.i18next.t('pmwjs_text_to_speech_daily_limit_reached_error_message'),
        type: PANEL_MESSAGE_TYPE.ERROR,
      };
    }
    return undefined;
  }, [errorState, props.isFullScreen]);
  const aiVoicePanelGenerationState = useAppSelector((state): AiVoicePanelGenerationState | undefined => {
    return state.panels.panelHashmap[panelId.current]?.data?.[AiVoicePanelDataKey.GENERATION_STATE] as AiVoicePanelGenerationState;
  });
  const selectedVoiceProfileId = useAppSelector((state) => {
    return state.userPreferences.selectedAiVoiceProfileId;
  });
  const activeVoiceProfileId =
    selectedVoiceProfileId !== null && getLanguageForVoiceId(selectedVoiceProfileId) !== null ? selectedVoiceProfileId : getDefaultVoiceIdForLanguage(DEFAULT_VOICE_LANGUAGE);
  const selectedLanguage = getLanguageForVoiceId(activeVoiceProfileId) ?? DEFAULT_VOICE_LANGUAGE;
  const isAiWriterOpen = useAppSelector((state): boolean => {
    return !!state.panels.panelHashmap[getAiVoicePanelId(panelId.current)]?.data?.[AiVoicePanelDataKey.IS_AI_WRITER_OPEN];
  });
  const setIsAiWriterOpen = (value: boolean): void => {
    dispatch(updatePanelData({panelId: getAiVoicePanelId(panelId.current), data: {[AiVoicePanelDataKey.IS_AI_WRITER_OPEN]: value}}));
  };
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, GENERATED_VOICE_GRID_ID);
  });
  const isAiVoiceGenerating = (): boolean => {
    return aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING;
  };
  const isAiWriterPanelOpen = isAiWriterOpen && !props.isFullScreen;

  const updatePanelHeight = (height: string): void => {
    dispatch(
      updatePanelSizeData({
        sizeDataId: panelId.current,
        sizeData: {
          height: height,
        },
      })
    );
  };

  const onBack = (e?: unknown): void => {
    if (!isAiWriterOpen && props.onBack) {
      props.onBack(e);
    } else if (isAiWriterPanelOpen) {
      setIsAiWriterOpen(false);
      updatePanelHeight(AI_VOICE_HEIGHT);
    }
  };

  const onClosePanel = (): void => {
    if (aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING) {
      showLoading(getLoadingKeyForAiVoice(panelId.current), {text: window.i18next.t('pmwjs_generating_audio')});
    }
    onClose();
  };

  const updateGenerationState = (state: AiVoicePanelGenerationState): void => {
    dispatch(
      updatePanelData({
        panelId: panelId.current,
        data: {
          [AiVoicePanelDataKey.GENERATION_STATE]: state,
        },
      })
    );
    updateGlobalGenerationState(state);
  };

  const updateGlobalGenerationState = (state: AiVoicePanelGenerationState): void => {
    let globalAiVoiceState: AiVoiceState;
    switch (state) {
      case AiVoicePanelGenerationState.IDLE:
        globalAiVoiceState = AiVoiceState.IDLE;
        break;
      case AiVoicePanelGenerationState.ERROR:
        globalAiVoiceState = AiVoiceState.ERROR;
        break;
      case AiVoicePanelGenerationState.GENERATING:
        globalAiVoiceState = AiVoiceState.GENERATING;
        break;
      case AiVoicePanelGenerationState.READY:
        globalAiVoiceState = AiVoiceState.READY;
        break;
      default:
        throw new Error(`Unhandled state for global ai voice state: ${state}`);
    }
    dispatch(updateAiVoiceState(globalAiVoiceState));
  };

  const getLoadingSkeleton = (): ReactElement<HTMLDivElement> => {
    return (
      <div className={`flexbox flex-align-items-center radius-12 gap-3 _full-width spacing-p-2 _cursor-not-allowed ${styles.skeletonContainer}`}>
        <SkeletonBrick isPulsating width={48} height={48} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} className="radius-12" />
        <div className="flex-column-align-start flex-1 gap-1 _overflow-hidden">
          <Text className="-truncated-text content-disabled _full-width spacing-m-b-1" bold size={TextSize.XXSMALL} val={state.prompt.trim()} />
          <SkeletonBrick isPulsating width={74} height={12} className="radius-4" />
        </div>
      </div>
    );
  };

  const onGenerateVoice = async (): Promise<void> => {
    setErrorState(undefined);
    updateGenerationState(AiVoicePanelGenerationState.GENERATING);

    const loadingItemId = uuidv4();
    dispatch(
      addItemsToGrid({
        id: GENERATED_VOICE_GRID_ID,
        items: [
          {
            type: GRID_ITEM_TYPE.CUSTOM,
            id: loadingItemId,
            children: getLoadingSkeleton(),
          },
        ],
        index: 0,
      })
    );
    try {
      const response = await generateSpeech({text: state.prompt.trim(), voiceId: activeVoiceProfileId});
      actions.update({creditsPill: {remainingCredits: response.remainingCredits}});
      const gridItem = {
        type: GRID_ITEM_TYPE.CUSTOM,
        id: `${response.id}`,
        children: <GeneratedVoiceItem {...getGeneratedVoiceItemProps(response, getUrlForGeneratedAiSpeech(response.filename))} />,
      };
      dispatch(addItemsToGrid({id: GENERATED_VOICE_GRID_ID, items: [gridItem], index: 0}));
      removeLoadingItem(loadingItemId);
      updateGenerationState(AiVoicePanelGenerationState.READY);
      setTimeout(() => {
        updateGenerationState(AiVoicePanelGenerationState.IDLE);
      }, 300);
      hideLoading(getLoadingKeyForAiVoice(panelId.current));
    } catch (e) {
      removeLoadingItem(loadingItemId);
      onGenerateVoiceError(e);
    }
  };

  const removeLoadingItem = (itemId: string): void => {
    dispatch(deleteGridItems({gridId: GENERATED_VOICE_GRID_ID, gridSelectionGroupId: GENERATED_VOICE_GRID_ID, itemIdsToDelete: [itemId]}));
  };

  const onGenerateVoiceError = (e: unknown): void => {
    updateGenerationState(AiVoicePanelGenerationState.ERROR);
    const errorMessage = getErrorMessage(e);

    if (errorMessage === GENERATE_SPEECH_DAILY_LIMIT_REACHED) {
      trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_LIMIT_REACHED);
      setErrorState(AiVoicePanelErrorState.DAILY_LIMIT_REACHED);
      return;
    }

    if (errorMessage.includes(GENERATE_SPEECH_INSUFFICIENT_CREDITS)) {
      openOutOfCreditsDialog();
      return;
    }

    openErrorModal({message: window.i18next.t('pmwjs_internet_error_message')});
  };

  const openOutOfCreditsDialog = (): void => {
    triggerBuyMoreCredits({
      headingText: window.i18next.t(isPremiumPlusUser() ? 'pmwjs_purchase_more_credits_for_ai' : 'pmwjs_purchase_more_credits_for_ai_with_upgrade_message'),
      modalTitle: window.i18next.t('pmwjs_youre_out_of_credits'),
      cb: (credits?: number): void => {
        if (credits === undefined || credits <= 0) {
          return;
        }
        actions.update({creditsPill: {remainingCredits: credits}});
        void onGenerateVoice();
      },
    });
  };

  const onCTAClick = async (): Promise<void> => {
    return isUserPremium() ? onGenerateVoice() : showTextToSpeechUpsellDialog();
  };

  const shouldShowHeaderBorder = (): boolean => {
    if (props.isFullScreen) {
      return true;
    }
    return topShadow;
  };

  const getTitle = (): string => {
    if (isAiWriterPanelOpen) {
      return window.i18next.t('pmwjs_ai_writer');
    }
    return isAiVoiceGenerating() ? `${window.i18next.t('pmwjs_generating')}...` : window.i18next.t('pmwjs_ai_voice');
  };

  const getHeader = (): ReactElement<PanelHeaderProps> | null => {
    if (isAiWriterPanelOpen) {
      return null;
    }

    return (
      <PanelHeader
        backButtonIconSize={isOpenedInPopOver ? IconSize.SIZE_ICON_16 : undefined}
        titleClasses={`_fit-width content-ai ${props.isFullScreen ? 'body-l-bold' : 'body-md-bold'}`}
        leftIconProps={props.isFullScreen ? {icon: 'icon-ai-voice', size: IconSize.SIZE_ICON_20, className: `content-ai ${showBackButton ? '' : 'spacing-m-l-10'}`} : undefined}
        className={`${shouldShowHeaderBorder() ? '' : '_borderless'} ${isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
        title={getTitle()}
        showCloseButton={(props.isFullScreen ?? false) || isMobile}
        onClose={onClosePanel}
        showBackButton={showBackButton}
        onBackButton={onBack}
        forceCenterAlignTitleWithActionItems
        isOpenedInModal={isMobile}
      />
    );
  };

  // No open tracking here: on desktop the audio add itself reveals the timeline and reports the
  // open as `audio_added` (AudioClips reveal) before this resolves; on mobile the timeline is
  // counted once per load as `mobile_auto` — so a panel-sourced open never fires on either.
  const onAddSelected = (): void => {
    void addSelectedVoicesToDesign();
  };

  const addSelectedVoicesToDesign = async (): Promise<void> => {
    const selectedItemIds = selectedGridItems.map((item) => item.id);
    if (props.onAddAudioItems) {
      await props.onAddAudioItems(selectedItemIds);
    } else {
      for (const itemId of selectedItemIds) {
        // eslint-disable-next-line no-await-in-loop -- parallel adds would race the poster's audio timeline mutation
        await props.onAddAudioItem(itemId);
      }
    }
    window.posterEditor?.whiteboard?.updateIsTimelineMode(true);
  };

  const getFooter = (): ReactElement<PanelFooterProps> | null => {
    if (selectedGridItems.length === 0 || !(isMobile || props.isFullScreen)) {
      return null;
    }
    return (
      <PanelFooter
        fullWidthPrimaryActions={isMobile}
        primaryActions={[<PanelFooterPrimaryDefaultAction key="add-selected" text={window.i18next.t('pmwjs_add_selected')} onClick={onAddSelected} />]}
      />
    );
  };

  const openAiWriterPanel = (): void => {
    setIsAiWriterOpen(!isAiWriterOpen);
    updatePanelHeight(isAiWriterOpen ? AI_VOICE_HEIGHT : AI_VOICE_WRITER_HEIGHT);
  };

  const openAiWriterPopover = (): void => {
    setIsAiWriterOpen(true);
  };

  const getAiWriterButton = (onClick: () => void, buttonRef?: RefObject<HTMLButtonElement | null>): ReactElement => {
    return (
      <AiButton
        data-testid={PosterEditorTestId.AI_VOICE_WRITE_BUTTON}
        buttonRef={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }}
        variant={AIButtonVariant.SUBDUED}
        text={window.i18next.t('pmwjs_help_me_write')}
        size={Size.XSMALL}
        shape={AIButtonShape.ROUND}
        icon="icon-ai-caption"
        disabled={isAiVoiceGenerating()}
        textClasses={'body-sm-bold'}
        customClasses="spacing-p-t-2 spacing-p-b-2"
        className={'radius-round'}
      />
    );
  };

  const renderAiWriterPopoverView = (): ReactElement | null => {
    if (!isAiWriterOpen) {
      return null;
    }
    return (
      <AiWriterPopoverView
        isOpen={isAiWriterOpen}
        referenceElementRef={aiWriterButtonRef}
        onClose={() => {
          setIsAiWriterOpen(false);
        }}
        onContentSelected={onAiWriterContentSelected}
      />
    );
  };

  const getAiPromptBoxOptions: FooterLeftAction = (): ReactElement => {
    if (props.isFullScreen) {
      return (
        <>
          {getAiWriterButton(openAiWriterPopover, aiWriterButtonRef)}
          {renderAiWriterPopoverView()}
        </>
      );
    }
    return getAiWriterButton(openAiWriterPanel);
  };

  const handleProfileSelected = (id: number): void => {
    updateSelectedAiVoiceProfileId(id);
  };

  const handleLanguageSelected = (language: VoiceLanguage): void => {
    if (language !== selectedLanguage) {
      updateSelectedAiVoiceProfileId(getDefaultVoiceIdForLanguage(language));
    }
  };

  const onAiWriterContentSelected = (text: string): void => {
    actions.update({
      prompt: text,
    });
    if (isAiWriterOpen) {
      setIsAiWriterOpen(false);
    }
  };

  const getPanelStateSpecificStyles = (): string => {
    if (isAiWriterPanelOpen) {
      return 'spacing-p-0 spacing-p-r-0 spacing-p-l-0 radius-12';
    }
    return `spacing-p-r-4 spacing-p-l-4 spacing-p-b-4 ${props.contentClassname ?? ''}`;
  };

  const getInnerContent = (): ReactElement => {
    if (isAiWriterPanelOpen) {
      return <AiWriterPanelView isOpenedInPopOver={isOpenedInPopOver} onBack={onBack} onClose={onClose} onContentSelected={onAiWriterContentSelected} />;
    }
    return (
      <AiVoiceView
        isGenerating={isAiVoiceGenerating()}
        errorState={errorState}
        selectedVoiceProfileId={activeVoiceProfileId}
        selectedLanguage={selectedLanguage}
        onCTAClick={onCTAClick}
        onAddAudioItem={(itemId) => props.onAddAudioItem(itemId)}
        enableDragToCanvas={props.enableDragToCanvas}
        onProfileSelected={handleProfileSelected}
        onLanguageSelected={handleLanguageSelected}
        getAiPromptBoxOptions={getAiPromptBoxOptions}
        isFullScreen={props.isFullScreen}
        maxSelectionNumber={props.maxSelectionNumber}
        panelId={panelId.current}
      />
    );
  };

  const getContent = (): ReactElement<PanelContentProps> => {
    return (
      <PanelContent className={`${styles.scrollableContent} ${getPanelStateSpecificStyles()}`} ref={gridRef}>
        {getInnerContent()}
      </PanelContent>
    );
  };

  useEffect(() => {
    updateGenerationState(AiVoicePanelGenerationState.IDLE);
    getRemainingAiUsages()
      .then((remainingCredits) => {
        actions.update({creditsPill: {remainingCredits}});
      })
      .catch(() => {
        window.PMW.log('AI voice panel: failed to fetch remaining AI credits');
      });
  }, []);

  useEffect(() => {
    actions.update({creditsPill: {actionCost: getCreditCostForVoiceId(activeVoiceProfileId)}});
  }, [activeVoiceProfileId]);

  return (
    <Panel
      ref={panelRef}
      panelId={panelId.current}
      data-testid={PosterEditorTestId.AI_VOICE_PANEL}
      className={'ct-neutral-primary'}
      key={panelId.current}
      panelHeader={getHeader()}
      panelContent={getContent()}
      panelFooter={getFooter()}
      panelMessage={getPanelMessage}
    />
  );
}
